import { message } from 'antd'
import { Model } from 'dva'
import { OrganisationsService } from '../../Service'
import { IsObject } from '../../Utils/helpers'
import { FailureHandler } from 'Utils/ApiHelper'
import { Path } from './types'

const Paths: Model = {
  namespace: 'paths',
  state: {
    list: [], // Should contain API style responses
    selectedPath: null,
    setPathLoading: false,
    loading: true,
    editCurrentTab: 'general',
    highlightedPath: null,
  },
  effects: {
    *fetchPaths(
      { payload = {}, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const version = yield select((state: any) => state.flowLayout.version?.version)
      const subFlow = yield select((state: any) => state.flowLayout.subFlow?.id)
      try {
        const response = yield call(OrganisationsService.organisationsFlowsVersionsPathsList, {
          ...payload,
          organisationUuid,
          flowUuid: flowUuid,
          flowConfigVersion: version,
          sourceBlockSubflow: subFlow,
        })
        onSuccess(response)
        yield put({ type: 'modifyList', payload: response })
      } catch (err) {
        FailureHandler('Error retrieving paths', err, onFailure)
      }
      setLoading(false)
    },
    *create(
      { setLoading = () => {}, onSuccess = () => {}, onFailure = () => {}, payload },
      { call, put, select }
    ): any {
      setLoading(true)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const subFlowId = yield select((state: any) => state.flowLayout.subFlow?.id)
      const flowUUID = yield select((state: any) => state.flowLayout.flow.uuid)
      const flowVersion = yield select((state: any) => state.flowLayout.version.version)
      const blockList = yield select((state: any) => state.blocks.list)

      let response
      let sourceBlock = blockList.filter(
        (block: any) => String(block.id) === String(payload.source_block)
      )
      let requestBody
      if (sourceBlock[0]?.type === 'Start') {
        requestBody = {
          ...payload,
          name: 'Start',
          subflow: subFlowId,
        }
      } else {
        requestBody = {
          ...payload,
          subflow: subFlowId,
        }
      }
      try {
        response = yield call(OrganisationsService.organisationsFlowsVersionsPathsCreate, {
          organisationUuid,
          flowUuid: flowUUID,
          flowConfigVersion: flowVersion,
          requestBody,
        })
        onSuccess(response)
        yield put({
          type: 'addPathToList',
          payload: response,
        })
      } catch (e: any) {
        FailureHandler('Error adding path.', e, onFailure)
      }
      setLoading(false)
    },
    *partialUpdate(
      { setLoading = () => {}, onSuccess = () => {}, onFailure = () => {}, payload, pathId },
      { call, put, select }
    ): any {
      setLoading(true)
      const flowLayout = yield select((state: any) => state.flowLayout)
      try {
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsPathsPartialUpdate,
          {
            organisationUuid: flowLayout.flow?.organisation?.uuid,
            flowUuid: flowLayout.flow.uuid,
            flowConfigVersion: flowLayout.version.version,
            id: pathId,
            requestBody: payload,
          }
        )
        onSuccess(response)
        yield put({ type: 'updateSelectedPathAndList', payload: response })
      } catch (e: any) {
        FailureHandler('Error updating path', e, onFailure)
      }
      setLoading(false)
    },
    *destroy(
      { onSuccess = () => {}, onFailure = () => {}, payload, undoRedoType },
      { call, put, select }
    ): any {
      if (payload.type === 'Start') {
        message.error('Cannot delete the Start path!')
        return
      }
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)
      try {
        const response = yield call(OrganisationsService.organisationsFlowsVersionsPathsDestroy, {
          organisationUuid,
          flowUuid,
          flowConfigVersion,
          id: payload.id,
        })
        onSuccess(response)
        yield put({ type: 'removePathsFromList', payload: [payload] })
      } catch (e: any) {
        FailureHandler('Error deleting the path.', e, onFailure)
      }
    },
    *unDeletePath(
      { onSuccess = () => {}, onFailure = () => {}, payload, undoRedoType },
      { call, put, select }
    ): any {
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)
      try {
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsPathsUndeleteRetrieve,
          {
            organisationUuid,
            flowUuid,
            flowConfigVersion,
            id: payload.id,
          }
        )
        onSuccess(response)
        yield put({
          type: 'addPathToList',
          payload: response,
        })
      } catch (e: any) {
        FailureHandler('Error restoring the path.', e, onFailure)
      }
    },
    *retrieve(
      { setLoading = () => {}, onSuccess = () => {}, onFailure = () => {}, payload },
      { call, put, select }
    ): any {
      setLoading(true)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)

      try {
        const response = yield call(OrganisationsService.organisationsFlowsVersionsPathsRetrieve, {
          organisationUuid,
          flowUuid,
          flowConfigVersion,
          id: payload,
        })
        onSuccess(response)
      } catch (e: any) {
        FailureHandler('Error fetching path.', e, onFailure)
      }
      yield put({ type: 'flowBuilder/clearRedo', payload: {} })
      setLoading(false)
    },
  },
  reducers: {
    setHighlightedPath(state, action) {
      return {
        ...state,
        highlightedPath: action.payload,
      }
    },
    clearSelectedPath(state) {
      return {
        ...state,
        selectedPath: null,
      }
    },
    toggleLoading(state, action) {
      return {
        ...state,
        loading: action.payload,
      }
    },
    toggleSetPathLoading(state, action) {
      return {
        ...state,
        setPathLoading: action.payload,
      }
    },
    savePathsList(state, action) {
      return {
        ...state,
        list: action.payload,
      }
    },
    setSelectedPath(state, action) {
      return {
        ...state,
        selectedPath: action.payload,
      }
    },
    updateSelectedPath(state, action) {
      return {
        ...state,
        selectedPath: {
          ...state.selectedPath,
          ...action.payload,
        },
      }
    },
    updateSelectedPathAndList(state, action) {
      return {
        ...state,
        selectedPath:
          String(state.selectedPath?.id) === String(action.payload.id)
            ? action.payload
            : state.selectedPath,
        list: state.list.map((path: any) => {
          if (String(action.payload?.id) === String(path.id)) {
            return action.payload
          }
          return path
        }),
      }
    },
    addPathToList(state, action) {
      return {
        ...state,
        list: [...state.list, action.payload],
      }
    },
    addPathsToList(state, action) {
      return {
        ...state,
        list: [...state.list, ...action.payload],
      }
    },
    removePathsFromList(state, action) {
      return {
        ...state,
        list: state.list.filter(
          (path: any) => !action.payload.map((b: any) => String(b.id)).includes(String(path.id))
        ),
      }
    },
    addBlockPathsToList(state, action) {
      return {
        ...state,
        list: [
          ...state.list.filter(
            (path: Path) => !action.payload.map((path: Path) => path.id).includes(path.id)
          ),
          ...action.payload,
        ],
      }
    },
    setEditCurrentTab(state, action) {
      return {
        ...state,
        editCurrentTab: action.payload,
      }
    },

    removeBlockPathsFromList(state, action) {
      return {
        ...state,
        list: state.list.filter((path: any) => {
          if (String(path.source_block?.id) === String(action.payload)) {
            return false
          }
          if (String(path.destination_block?.id) === String(action.payload)) {
            return false
          }
          return true
        }),
      }
    },
    modifyList(state, action) {
      // clone the list
      let list = [...state.list]
      if (IsObject(action.payload)) {
        // if the payload is an object, it's a single object, so add it to the list
        list = list.filter((l: any) => l.id !== action.payload.id)
        list.push(action.payload)
      } else if (Array.isArray(action.payload)) {
        // if the payload is an array, it's a list of objects, so add them to the list
        list = list
          .filter((l: any) => !action.payload.map((p: any) => p.id).includes(l.id))
          .concat(action.payload)
      } else if (Number.isInteger(action.payload)) {
        // if the payload is an integer, it's an id sent in delete operations, so remove it from the list
        list = list.filter((l: any) => l.id !== action.payload)
      }
      return {
        ...state,
        list: list,
      }
    },
    resetPaths(state, action) {
      return {
        ...state,
        list: [],
        selectedPath: null,
        setPathLoading: false,
        loading: true,
        editCurrentTab: 'general',
        highlightedPath: null,
      }
    },
  },
}

export default Paths
